import React from "react";
import { Observer, observer } from "mobx-react-lite";
import { useScreenDimensions, useDevice } from "../_layout";
import { useTranslation } from "react-i18next";
import { useState, useEffect, useRef } from "react";
import {
  StyleSheet,
  Text,
  View,
  TouchableOpacity,
  ScrollView,
  Linking,
} from "react-native";
import { Menu, MenuItem } from "react-native-material-menu";
import {
  Ionicons,
  MaterialCommunityIcons,
  MaterialIcons,
  Feather,
} from "@expo/vector-icons";
import Colors from "../../Resources/Color";
import GlobalStyles from "../../Resources/Styles";
import Fonts from "../../Resources/Fonts";
import WhitePng from "../../assets/EFORT COACH WHITE SVG.svg";
import SearchBar from "./SearchBar";
import NoTranslateText from "./NoTranslateText";
import SyncStatusIndicator from "./SyncStatusIndicator";
import ClientWeekStatusText from "./ClientWeekStatusText";
import { SyncStatus } from "../../Resources/Constants";
import Tooltip from "./Tooltip";
import MediaManager from "../Classes/MediaManager";
import { LinearGradient } from "expo-linear-gradient";
import { useDrag, useDrop } from "react-dnd";
import { getAppVersion, compareVersions } from "../utils/version";
import SvgImage from "./SvgImage";

const FreeTrialCountdown = React.memo(
  ({ trialStartTimestamp, onUpgradePress, t, isMobile }) => {
    const CountdownIndicator = React.memo(({ startTimestamp }) => {
      const [percentageFilled, setPercentageFilled] = useState(0);
      const [daysLeft, setDaysLeft] = useState(0);

      useEffect(() => {
        // No valid start date → keep defaults, no interval, no crash
        if (!startTimestamp) {
          setPercentageFilled(0);
          setDaysLeft(0);
          return;
        }

        const startDate = new Date(startTimestamp);
        const trialPeriodEndDate = new Date(startTimestamp);
        trialPeriodEndDate.setDate(trialPeriodEndDate.getDate() + 10);

        const totalDuration =
          trialPeriodEndDate.getTime() - startDate.getTime();

        // Defensive: weird dates → just mark as finished
        if (totalDuration <= 0) {
          setPercentageFilled(100);
          setDaysLeft(0);
          return;
        }

        const updatePercentage = () => {
          const now = new Date();
          const elapsed = now.getTime() - startDate.getTime();
          const remaining = trialPeriodEndDate.getTime() - now.getTime();

          const percentage = Math.min(
            Math.max((elapsed / totalDuration) * 100, 0),
            100
          );
          const days = Math.max(
            Math.ceil(remaining / (1000 * 60 * 60 * 24)),
            0
          );

          setPercentageFilled(percentage);
          setDaysLeft(days);
        };

        updatePercentage();
        const intervalId = setInterval(updatePercentage, 24 * 60 * 60 * 1000);

        return () => clearInterval(intervalId);
      }, [startTimestamp]);

      // Optional: hide the whole thing if there's no trial
      if (!startTimestamp) {
        return null;
      }

      return (
        <View style={{ marginBottom: 12 }}>
          <Text style={{ color: "white", marginBottom: 8 }}>
            {t(
              daysLeft === 1
                ? "freeTrialDayLeft-string"
                : "freeTrialDaysLeft-string",
              { daysLeft }
            )}
          </Text>
          <View
            style={{
              height: 8,
              width: "100%",
              backgroundColor: "#D1D5DB",
              borderRadius: 6,
              overflow: "hidden",
            }}
          >
            <View
              style={{
                height: "100%",
                backgroundColor: Colors.bubbleColor,
                width: `${percentageFilled}%`,
                borderRadius: 6,
              }}
            />
          </View>
        </View>
      );
    });

    const SeeRatesButton = React.memo(({ onPress, isMobile }) => {
      const [isHovered, setIsHovered] = useState(false);

      return (
        <TouchableOpacity
          onPress={onPress}
          style={{ borderRadius: 14 }}
          onMouseEnter={() => !isMobile && setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <View
            style={{
              backgroundColor: Colors.bubbleColor,
              borderRadius: 14,
              borderColor: Colors.bubbleColor,
              height: 40,
              justifyContent: "center",
              alignItems: "center",
              opacity: isHovered ? 0.7 : 1,
              paddingHorizontal: 10,
            }}
          >
            <Text style={{ textAlign: "center", color: Colors.whiteColor }}>
              {t("upgradePlan-string")}
            </Text>
          </View>
        </TouchableOpacity>
      );
    });

    return (
      <View>
        <CountdownIndicator startTimestamp={trialStartTimestamp} />
        <SeeRatesButton onPress={onUpgradePress} isMobile={isMobile} />
      </View>
    );
  }
);

const SideMenu = ({ coach }) => {
  const { screenSize } = useScreenDimensions();
  const { isMobile } = useDevice();
  const { t, i18n } = useTranslation();

  const [isConfigMode, setIsConfigMode] = useState(false);
  const [isAppPartsHovered, setIsAppPartsHovered] = useState(false);
  const [tempSidebarConfig, setTempSidebarConfig] = useState([]);

  // Get the active sidebar configuration (from coach or default)
  const getActiveSidebarConfig = () => {
    // Define allowed sidebar item IDs
    const allowedIds = [
      "CoachDashboard",
      "CoachLibrary",
      "CoachBilling",
      "CoachCompetitions",
      "CoachApplications",
      "CoachNutrition",
      "CoachSettings",
    ];

    // Start with Dashboard at the top (always present, not stored)
    const config = [{ id: "CoachDashboard", visible: true, order: 0 }];

    if (coach.sidebarConfig && coach.sidebarConfig.length > 0) {
      // Add the stored configurable items (Library, Billing, Competitions, Applications, Nutrition)
      // Filter out any Dashboard, Settings, or invalid items
      const middleItems = coach.sidebarConfig.filter(
        (item) =>
          item.id !== "CoachDashboard" &&
          item.id !== "CoachSettings" &&
          allowedIds.includes(item.id)
      );

      // Check if Library is present in the stored config
      const hasLibrary = middleItems.some((item) => item.id === "CoachLibrary");

      // If Library is missing, add it at the beginning of middle items
      if (!hasLibrary) {
        middleItems.unshift({ id: "CoachLibrary", visible: true, order: 0 });
      }

      // Ensure they're sorted by their order property
      const sortedConfig = [...middleItems].sort((a, b) => a.order - b.order);

      // Force Library to be visible (it's mandatory)
      sortedConfig.forEach((item) => {
        if (item.id === "CoachLibrary") {
          config.push({ ...item, visible: true });
        } else {
          config.push({ ...item });
        }
      });
    } else {
      // Use default middle items if no config is stored
      const defaultMiddleItems = [
        { id: "CoachLibrary", visible: true, order: 1 },
        { id: "CoachBilling", visible: true, order: 2 },
        { id: "CoachCompetitions", visible: true, order: 3 },
        { id: "CoachApplications", visible: true, order: 4 },
      ];

      // Add Nutrition if allowed
      if (coach.allowedNutrition) {
        defaultMiddleItems.push({
          id: "CoachNutrition",
          visible: true,
          order: 5,
        });
      }

      config.push(...defaultMiddleItems);
    }

    // Add Settings at the bottom (always present, not stored)
    config.push({ id: "CoachSettings", visible: true, order: config.length });

    // Update order properties to match actual positions
    config.forEach((item, idx) => {
      item.order = idx;
    });

    return config;
  };

  const ClientRow = observer(({ client }) => {
    const toggleIsChecked = () => {
      if (!coach.isLoading && coach.isActive) {
        client.setProperty("isChecked", !client.isChecked);
        client.uploadData({
          is_checked: client.isChecked,
        });
      }
    };
    const [isHovered, setIsHovered] = useState(false);
    const [isCheckHovered, setIsCheckHovered] = useState(false);
    return (
      <View
        style={{
          flexDirection: "row",
          flex: 1,
          padding: 8,
          marginHorizontal: 4,
          marginVertical: 2,
          alignItems: "center",
          borderRadius: 14,
          backgroundColor:
            client.id === coach.selectedClientId
              ? "rgba(255, 255, 255, 0.2)"
              : isHovered
              ? "rgba(255, 255, 255, 0.1)"
              : "transparent",
          paddingVertical: 8,
          opacity: client.isAvailable ? 1 : 0.5,
        }}
        onMouseEnter={() => !isMobile && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <TouchableOpacity
          onPress={toggleIsChecked}
          key={client.id}
          onMouseEnter={() => !isMobile && setIsCheckHovered(true)}
          onMouseLeave={() => setIsCheckHovered(false)}
          style={{ marginRight: 8 }}
        >
          <MaterialIcons
            name={client.isChecked ? "check-box" : "check-box-outline-blank"}
            size={24}
            color={Colors.whiteColor}
            style={{ opacity: isCheckHovered && client.isChecked ? 0.8 : 1 }}
          />
          {isCheckHovered && !client.isChecked && (
            <MaterialIcons
              name="check"
              size={16}
              color={Colors.whiteColor}
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: [{ translateX: -8 }, { translateY: -8 }],
                opacity: 0.8,
              }}
            />
          )}
        </TouchableOpacity>
        <TouchableOpacity
          onPress={() => {
            if (client.isAvailable) {
              coach.selectClient(client.id, t, i18n);
            } else {
              if (client.coach) {
                coach.setProperty("sideMenuVisible", false);
                coach.setProperty("showingOfflineAlert", true);
              }
            }
          }}
          style={{ flex: 1 }}
        >
          <View
            style={{
              alignItems: "flex-start",
              width: "100%",
            }}
          >
            <Text
              style={{
                color: Colors.whiteColor,
                fontSize: 14,
              }}
              numberOfLines={1}
            >
              {client.nameForCoach} {client.surnameForCoach}
            </Text>
            <ClientWeekStatusText
              client={client}
              style={{
                color: Colors.grayColor,
                fontSize: 11,
                marginTop: 2,
              }}
              showColorForUnprepared={true}
            />
          </View>
        </TouchableOpacity>

        {client.isFetched &&
          client.id != client.coach.selectedClientId &&
          client.syncStatusManager.syncStatus != SyncStatus.SYNCED && (
            <View style={{ marginLeft: 8 }}>
              <SyncStatusIndicator
                syncStatusManager={client.syncStatusManager}
                white={true}
              />
            </View>
          )}
      </View>
    );
  });

  const [sortingMenu, setSortingMenu] = useState(false);
  const showSortingMenu = () => setSortingMenu(true);
  const [weekDaysMenu, setWeekDaysMenu] = useState(false);

  const selectDay = () => {
    setSortingMenu(false);
    setWeekDaysMenu(true);
  };

  // const selectClient = (clientId) => {
  //   if (!coach.isLoading && coach.isActive) {
  //     coach.setProperty("showingCoachView", "ClientDashboard");
  //     if (coach.teamCoach) {
  //       coach.teamCoach.setProperty("selectedClientId", clientId);
  //       coach.teamCoach.fetchNewClient(clientId, t, i18n.language);
  //     } else {
  //       coach.setProperty("selectedClientId", clientId);
  //       coach.fetchNewClient(clientId, t, i18n.language);
  //     }

  //     if (coach.chatIsExpanded) {
  //       coach.setProperty("chatIsExpanded", false);
  //       coach.setProperty("showingChatList", false);
  //       const mediaManager = MediaManager.getInstance();
  //       mediaManager?.closeMediaPlayer();
  //     }
  //     coach.setProperty("sideMenuVisible", false);
  //   }
  // };

  const WeekDaysMenuSelector = observer(() => {
    const [hoveredMenuItem, setHoveredMenuItem] = useState(null);

    return (
      <Menu
        visible={weekDaysMenu}
        style={GlobalStyles.menu}
        onRequestClose={() => setWeekDaysMenu(false)}
      >
        {Array.from({ length: 7 }, (_, index) => (
          <MenuItem
            key={index}
            onPress={() =>
              coach.setProperty("filterConditionSideMenuDay", index)
            }
            style={{ minWidth: 150 }}
            onMouseEnter={() => !isMobile && setHoveredMenuItem(index)}
            onMouseLeave={() => setHoveredMenuItem(null)}
            textStyle={{
              fontSize: 14,
              fontWeight:
                (coach.filterConditionSideMenuDay === index && "700") ||
                (hoveredMenuItem === index && "600"),
              color: Colors.blackColor,
            }}
          >
            {index === 0
              ? t("monday-string")
              : index === 1
              ? t("tuesday-string")
              : index === 2
              ? t("wednesday-string")
              : index === 3
              ? t("thursday-string")
              : index === 4
              ? t("friday-string")
              : index === 5
              ? t("saturday-string")
              : t("sunday-string")}
          </MenuItem>
        ))}
      </Menu>
    );
  });

  const SortingMenu = observer(() => {
    const [hoveredMenuItem, setHoveredMenuItem] = useState(null);

    return (
      <Menu
        visible={sortingMenu}
        anchor={
          <Tooltip
            content="filterAthletes-string"
            placement="top"
            delayedOpen={true}
            disabled={sortingMenu}
          >
            <TouchableOpacity
              onPress={!coach.isLoading && showSortingMenu}
              disabled={coach.isLoading || !coach.isActive}
              onMouseEnter={() => !isMobile && setHoveredMenuItem("filter")}
              onMouseLeave={() => setHoveredMenuItem(null)}
            >
              <Ionicons
                name="filter"
                size={12}
                color={
                  coach.getFilterConditionSideMenu === "EmpiezaSemana" ||
                  coach.getFilterConditionSideMenu === "TerminaSemana"
                    ? coach.filterConditionSideMenuDay !== null
                      ? Colors.bubbleColor
                      : Colors.whiteColor
                    : coach.getFilterConditionSideMenu === ""
                    ? Colors.whiteColor
                    : Colors.bubbleColor
                }
                style={{ opacity: hoveredMenuItem === "filter" ? 0.8 : 1 }}
              />
            </TouchableOpacity>
          </Tooltip>
        }
        style={[
          GlobalStyles.menu,
          { width: screenSize !== "large" ? 220 : 300 },
        ]}
        onRequestClose={() => setSortingMenu(false)}
      >
        {[
          { label: "all-string", value: "" },
          { label: "pendingClients-string", value: "Pending" },
          { label: "completedClients-string", value: "Complete" },
          { label: "clientsWithBlockToCreate-string", value: "Bloque" },
          { label: "clientsWithWeekToUpdate-string", value: "Semana" },
          { label: "competitors-string", value: "Competidores" },
          { label: "nonCompetitors-string", value: "NoCompetidores" },
          {
            label: "startsWeekIn-string",
            value: "EmpiezaSemana",
            action: selectDay,
          },
          {
            label: "endsWeekIn-string",
            value: "TerminaSemana",
            action: selectDay,
          },
        ].map((item) => (
          <MenuItem
            key={item.value}
            onPress={() => {
              item.action?.();
              coach.setFilterConditionSideMenu(item.value);
            }}
            style={{ minWidth: screenSize !== "large" ? 220 : 300 }}
            onMouseEnter={() => !isMobile && setHoveredMenuItem(item.value)}
            onMouseLeave={() => setHoveredMenuItem(null)}
            textStyle={{
              fontWeight:
                (coach.filterConditionSideMenu === item.value && "700") ||
                hoveredMenuItem === item.value
                  ? "600"
                  : "normal",

              fontSize: 14,
              color: Colors.blackColor,
            }}
          >
            {t(item.label)}
          </MenuItem>
        ))}
      </Menu>
    );
  });

  const AppPartButton = observer(({ part }) => {
    const isSelected = coach.showingCoachView === part;
    const [isHovered, setIsHovered] = useState(false);
    const iconName =
      part === "CoachDashboard"
        ? "home-outline"
        : part === "CoachLibrary"
        ? "folder-outline"
        : part === "CoachBilling"
        ? "card-outline"
        : part === "CoachCompetitions"
        ? "podium-outline"
        : part === "CoachApplications"
        ? "person-outline"
        : part === "CoachNutrition"
        ? "nutrition-outline"
        : "settings-outline";

    const text =
      part === "CoachDashboard"
        ? "home-string"
        : part === "CoachLibrary"
        ? "library-string"
        : part === "CoachBilling"
        ? "billing-string"
        : part === "CoachCompetitions"
        ? "competitions-string"
        : part === "CoachApplications"
        ? "applications-string"
        : part === "CoachNutrition"
        ? "nutrition-string"
        : "settings-string";

    const action = () => {
      if (part === "CoachApplications") {
        if (
          !coach.isLoading &&
          coach.isActive &&
          !coach.isOffline &&
          !coach.teamCoach
        ) {
          coach.setProperty("showingCoachView", "CoachApplications");
          coach.setProperty("sideMenuVisible", false);
          coach.unselectClient();
          if (coach.chatIsExpanded) {
            coach.setProperty("chatIsExpanded", false);
            coach.setProperty("showingChatList", false);
            const mediaManager = MediaManager.getInstance();
            mediaManager?.closeMediaPlayer();
          }
        }
      } else {
        if (!coach.isLoading && coach.isActive) {
          coach.setProperty("showingCoachView", part);
          if (coach.chatIsExpanded) {
            coach.setProperty("chatIsExpanded", false);
            coach.setProperty("showingChatList", false);
            const mediaManager = MediaManager.getInstance();
            mediaManager?.closeMediaPlayer();
          }
          coach.setProperty("sideMenuVisible", false);
          coach.unselectClient();
        }
      }
    };
    return (
      <TouchableOpacity
        onPress={action}
        style={{
          flexDirection: "row",
          alignItems: "center",
          marginTop: part === "CoachDashboard" ? 32 : 16,
          opacity: part === "CoachDashboard" ? 1 : coach.teamCoach ? 0.5 : 1,
        }}
        onMouseEnter={() => !isMobile && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Ionicons
          name={iconName}
          size={18}
          color={
            isHovered
              ? "rgba(255, 255, 255, 0.9)"
              : isSelected
              ? Colors.whiteColor
              : Colors.grayColor
          }
          style={{ marginLeft: 16 }}
        />
        <View style={{ flexDirection: "row", alignItems: "center" }}>
          <Text
            style={{
              fontWeight: "450",
              fontSize: 15,
              marginLeft: 10,
              color: isHovered
                ? "rgba(255, 255, 255, 0.8)"
                : isSelected
                ? Colors.whiteColor
                : Colors.grayColor,
            }}
          >
            {t(text)}
          </Text>
          {part === "CoachApplications" && coach.applicationsHasUnread && (
            <View
              style={{
                width: 8,
                height: 8,
                borderRadius: 4,
                backgroundColor: Colors.lightGrayColor,
                marginLeft: 8,
              }}
            />
          )}
        </View>
      </TouchableOpacity>
    );
  });

  // const FreeTrialCountdown = React.memo(() => {
  //   // Extract and memoize the trial start date timestamp to prevent unnecessary re-renders
  //   const trialStartTimestamp = React.useMemo(() => {
  //     const startDate = coach?.trialPeriodStartDate;
  //     if (!(startDate instanceof Date) || isNaN(startDate.getTime())) {
  //       return null;
  //     }
  //     return startDate.getTime();
  //   }, [coach?.trialPeriodStartDate?.getTime()]);

  //   const CountdownIndicator = React.memo(({ startTimestamp }) => {
  //     const [percentageFilled, setPercentageFilled] = useState(0);
  //     const [daysLeft, setDaysLeft] = useState(0);

  //     useEffect(() => {
  //       // No valid start date → keep defaults, no interval, no crash
  //       if (!startTimestamp) {
  //         setPercentageFilled(0);
  //         setDaysLeft(0);
  //         return;
  //       }

  //       const startDate = new Date(startTimestamp);
  //       const trialPeriodEndDate = new Date(startTimestamp);
  //       trialPeriodEndDate.setDate(trialPeriodEndDate.getDate() + 10);

  //       const totalDuration =
  //         trialPeriodEndDate.getTime() - startDate.getTime();

  //       // Defensive: weird dates → just mark as finished
  //       if (totalDuration <= 0) {
  //         setPercentageFilled(100);
  //         setDaysLeft(0);
  //         return;
  //       }

  //       const updatePercentage = () => {
  //         const now = new Date();
  //         const elapsed = now.getTime() - startDate.getTime();
  //         const remaining = trialPeriodEndDate.getTime() - now.getTime();

  //         const percentage = Math.min(
  //           Math.max((elapsed / totalDuration) * 100, 0),
  //           100
  //         );
  //         const days = Math.max(
  //           Math.ceil(remaining / (1000 * 60 * 60 * 24)),
  //           0
  //         );

  //         setPercentageFilled(percentage);
  //         setDaysLeft(days);
  //       };

  //       updatePercentage();
  //       const intervalId = setInterval(updatePercentage, 24 * 60 * 60 * 1000);

  //       return () => clearInterval(intervalId);
  //     }, [startTimestamp]);

  //     // Optional: hide the whole thing if there’s no trial
  //     if (!startTimestamp) {
  //       return null;
  //     }

  //     return (
  //       <View style={{ marginBottom: 12 }}>
  //         <Text style={{ color: "white", marginBottom: 8 }}>
  //           {t(
  //             daysLeft === 1
  //               ? "freeTrialDayLeft-string"
  //               : "freeTrialDaysLeft-string",
  //             { daysLeft }
  //           )}
  //         </Text>
  //         <View
  //           style={{
  //             height: 8,
  //             width: "100%",
  //             backgroundColor: "#D1D5DB",
  //             borderRadius: 6,
  //             overflow: "hidden",
  //           }}
  //         >
  //           <View
  //             style={{
  //               height: "100%",
  //               backgroundColor: Colors.bubbleColor,
  //               width: `${percentageFilled}%`,
  //             }}
  //           />
  //         </View>
  //       </View>
  //     );
  //   });

  //   const SeeRatesButton = React.memo(() => {
  //     const [isHovered, setIsHovered] = useState(false);

  //     return (
  //       <TouchableOpacity
  //         onPress={() => {
  //           coach.setProperty("sideMenuVisible", false);
  //           coach.setProperty("showingStripePayment", true);
  //         }}
  //         style={{ borderRadius: 14 }}
  //         onMouseEnter={() => !isMobile && setIsHovered(true)}
  //         onMouseLeave={() => setIsHovered(false)}
  //       >
  //         <View
  //           style={{
  //             backgroundColor: Colors.bubbleColor,
  //             borderRadius: 14,
  //             borderColor: Colors.bubbleColor,
  //             height: 40,
  //             justifyContent: "center",
  //             alignItems: "center",
  //             opacity: isHovered ? 0.7 : 1,
  //             paddingHorizontal: 10,
  //           }}
  //         >
  //           <Text style={{ textAlign: "center", color: Colors.whiteColor }}>
  //             {t("upgradePlan-string")}
  //           </Text>
  //         </View>
  //       </TouchableOpacity>
  //     );
  //   });

  //   return (
  //     <View>
  //       <CountdownIndicator startTimestamp={trialStartTimestamp} />
  //       <SeeRatesButton />
  //     </View>
  //   );
  // });

  const UpgradePlanDiscount = observer(() => {
    const UpgradePlanButton = () => {
      const [isHovered, setIsHovered] = useState(false);

      return (
        <TouchableOpacity
          onPress={() => {
            coach.setProperty("sideMenuVisible", false);
            try {
              window.location.assign(
                "https://billing.stripe.com/p/login/9AQ3fW6andcNgne000"
              );
            } catch (error) {
              console.error("Error adding document: ", error);
            }
          }}
          style={{ opacity: isHovered ? 0.7 : 1 }}
          onMouseEnter={() => !isMobile && setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <View
            style={{
              backgroundColor: Colors.bubbleColor,
              borderRadius: 14,
              justifyContent: "center",
              borderColor: Colors.bubbleColor,
              height: 40,
              justifyContent: "center",
              alignItems: "center",
              opacity: 1,
            }}
          >
            <Text
              style={{
                textAlignVertical: "center",
                textAlign: "center",
                color: Colors.whiteColor,
                marginHorizontal: 10,
              }}
            >
              {t("upgradePlan-string")}
            </Text>
          </View>
        </TouchableOpacity>
      );
    };

    const [isHovered, setIsHovered] = useState(false);

    return (
      <View>
        <View
          style={{
            width: "100%",
            alignItems: "flex-end",
          }}
        >
          <TouchableOpacity
            onPress={() => coach.setProperty("showingUpgradePlan", false)}
            onMouseEnter={() => !isMobile && setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <Ionicons
              name="close"
              size={20}
              color="gray"
              style={{
                opacity: isHovered ? 0.5 : 1,
              }}
            />
          </TouchableOpacity>
        </View>
        <Text style={{ color: "white", marginBottom: 8 }}>
          {t("youHaveMaxClients-string", {
            clientsLength: coach.clients.length,
          })}
        </Text>
        <Text style={{ color: "white", marginBottom: 16 }}>
          {t("useUpgradeDiscount-string")}
        </Text>
        <UpgradePlanButton />
      </View>
    );
  });

  const ResetClientsTextButton = observer(() => {
    const [isHovered, setIsHovered] = useState(false);
    return (
      <Tooltip
        content="resetCompletedAthletes-string"
        placement="top"
        delayedOpen={true}
        // disabled={coach.isOffline}
      >
        <TouchableOpacity
          onPress={() => {
            if (!coach.isLoading && coach.isActive) {
              if (!coach.isOffline) {
                coach.setProperty("sideMenuVisible", false);
                coach.setProperty("showingResetAlert", true);
              }
            }
          }}
          style={{ opacity: isHovered ? 0.8 : 1 }}
          onMouseEnter={() => !isMobile && setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <Text
            style={{
              color: Colors.whiteColor,
              marginRight: 12,
              opacity: coach.isOffline ? 0.5 : 1,
              fontSize: 12,
            }}
          >
            {t("reset-string")}
          </Text>
        </TouchableOpacity>
      </Tooltip>
    );
  });

  const TeamCoachMenu = () => {
    const [isMenuHovered, setIsMenuHovered] = useState(false);
    const [teamMenu, setTeamMenu] = useState(false);
    const showTeamMenu = () => setTeamMenu(true);

    const MenuItemView = ({ teamCoach, index }) => {
      const [isHovered, setIsHovered] = useState(false);

      const fetchTeamCoachProva = async () => {
        coach.setProperty("showingCoachView", "CoachDashboard");
        coach.setProperty("searchTermSideMenu", "");
        coach.fetchTeamCoach(
          teamCoach.id,
          teamCoach.email,
          teamCoach.name,
          teamCoach.surname
        );
        setTeamMenu(false);
        setIsMenuHovered(false);
      };

      return index === 0 ? (
        <MenuItem
          onPress={() => {
            setTeamMenu(false);
            coach.setProperty("showingCoachView", "CoachDashboard");
            coach.setProperty("searchTermSideMenu", "");
            coach.setProperty("teamCoach", null);
            setIsMenuHovered(false);
          }}
          style={{ minWidth: 190 }}
          textStyle={{
            fontSize: 14,
            fontWeight: isHovered ? "600" : "normal",
          }}
          onMouseEnter={() => !isMobile && setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <View>
            <Text>
              {coach.name} {coach.surname}
            </Text>
            {!coach.isTeamCoach && (
              <Text style={{ color: Colors.grayColor, fontSize: 11 }}>
                Head Coach
              </Text>
            )}
          </View>
        </MenuItem>
      ) : (
        <MenuItem
          onPress={() => fetchTeamCoachProva()}
          style={{ minWidth: 190 }}
          textStyle={{
            fontSize: 14,
            fontWeight: isHovered ? "600" : "normal",
          }}
          onMouseEnter={() => !isMobile && setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <Text>
            {coach.name} {coach.surname}
          </Text>
        </MenuItem>
      );
    };
    return (
      coach.id === "jijijijij" && (
        <View style={{ marginHorizontal: 10, marginTop: 32 }}>
          <Menu
            visible={teamMenu}
            anchor={
              <TouchableOpacity
                onPress={showTeamMenu}
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                  paddingHorizontal: 10,
                  paddingVertical: 8,
                  borderRadius: 12,
                  backgroundColor: isMenuHovered
                    ? "rgba(172, 172, 172, 0.3)"
                    : "rgba(172, 172, 172, 0.2)",
                  width: "100%",
                }}
                onMouseEnter={() => !isMobile && setIsMenuHovered(true)}
                onMouseLeave={() => setIsMenuHovered(false)}
              >
                <View>
                  {coach.teamCoach ? (
                    <Text
                      style={{
                        color: "white",
                        fontSize: 14,
                        fontWeight: "500",
                      }}
                    >
                      {coach.teamCoach.name} {coach.teamCoach.surname}
                    </Text>
                  ) : (
                    <>
                      <Text
                        style={{
                          color: "white",
                          fontSize: 14,
                          fontWeight: "500",
                        }}
                      >
                        {coach.name} {coach.surname}
                      </Text>
                      <Text style={{ color: Colors.grayColor, fontSize: 11 }}>
                        Head Coach
                      </Text>
                    </>
                  )}
                </View>

                <Feather
                  name={teamMenu ? "chevron-up" : "chevron-down"}
                  size={20}
                  color="white"
                />
              </TouchableOpacity>
            }
            style={GlobalStyles.menu}
            onRequestClose={() => {
              setTeamMenu(false);
              setIsMenuHovered(false);
            }}
          >
            {coach.teamCoach && <MenuItemView index={0} />}
            {coach.teamCoaches
              .filter((c) =>
                coach.teamCoach ? coach.teamCoach.id !== c.id : true
              )
              .map((tCoach, idx) => (
                <MenuItemView teamCoach={tCoach} index={idx + 1} key={idx} />
              ))}
          </Menu>
        </View>
      )
    );
  };

  const TutorialsButton = () => {
    const [isHovered, setIsHovered] = useState(false);

    const handleOpenDocs = () => {
      if (!coach.isOffline) {
        const isSpanish = i18n.language === "es";
        const languagePrefix = isSpanish ? "/es" : "";
        const url = `https://app.efortcoach.com/docs${languagePrefix}/program/overview`;
        Linking.openURL(url);
      }
    };

    return (
      <TouchableOpacity
        onPress={handleOpenDocs}
        onMouseEnter={() => !isMobile && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <View
          style={{
            position: "relative",
          }}
        >
          {/* Top gradient border */}
          <LinearGradient
            colors={
              !isHovered
                ? ["rgba(0, 0, 0, 1)", "rgba(24, 24, 24, 1)"]
                : ["rgba(24, 24, 24, 1)", "rgba(24, 24, 24, 1)"]
            }
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 0 }}
            style={{
              paddingVertical: 1,
            }}
          >
            <View
              style={{
                alignItems: "center",
                flexDirection: "row",
                flex: 1,
                paddingVertical: 6,
                paddingHorizontal: 12,
              }}
            >
              <Ionicons name="school" size={16} color={Colors.whiteColor} />
              <Text
                style={{
                  fontSize: 14,
                  fontWeight: "400",
                  marginLeft: 6,
                  color: Colors.whiteColor,
                  // background:
                  //   "linear-gradient(90deg, rgba(250, 250, 250, 1) 0%, rgba(211, 211, 211, 1) 100%)",
                  // WebkitBackgroundClip: "text",
                  // WebkitTextFillColor: "transparent",
                  // backgroundClip: "text",
                }}
              >
                {t("tutorials-string")}
              </Text>
            </View>
          </LinearGradient>
        </View>
      </TouchableOpacity>
    );
  };

  const WhatsNewButton = observer(() => {
    const [isHovered, setIsHovered] = useState(false);

    return (
      <View
        style={{
          borderTopColor: "rgba(255, 255, 255, 0.2)",
          borderTopWidth: 1,
          backgroundColor: Colors.sideMenuColor,
          paddingHorizontal: 12,
          paddingVertical: 8,
        }}
      >
        <TouchableOpacity
          onPress={() => {
            coach.setProperty("showingWhatsNew", true);
          }}
          onMouseEnter={() => !isMobile && setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          style={{
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            opacity: isHovered ? 0.8 : 1,
          }}
        >
          <Text
            style={{
              fontSize: 14,
              fontWeight: "400",
              color: Colors.lightGrayColor,
            }}
          >
            {t("whatsNew-string")}
          </Text>
          <View
            style={{
              width: 8,
              height: 8,
              borderRadius: 4,
              backgroundColor: Colors.bubbleColor,
            }}
          />
        </TouchableOpacity>
      </View>
    );
  });

  const ConfigureSidebarButton = () => {
    const [isHovered, setIsHovered] = useState(false);

    return (
      <View style={{ alignItems: "flex-start" }}>
        <TouchableOpacity
          onPress={() => {
            setIsConfigMode(true);
            // Get the current active sidebar config (which has Dashboard and Settings)
            const activeConfig = getActiveSidebarConfig();

            // Use the full config for display (including Dashboard and Settings)
            let configToEdit = activeConfig.map((item) => ({ ...item })); // Deep copy each item

            // Define the mandatory middle items that must always be present
            const mandatoryMiddleItems = [
              "CoachLibrary",
              "CoachBilling",
              "CoachCompetitions",
              "CoachApplications",
            ];

            // Find the Settings index (always last)
            const settingsIndex = configToEdit.findIndex(
              (item) => item.id === "CoachSettings"
            );

            // Check and add any missing mandatory items before Settings
            mandatoryMiddleItems.forEach((itemId) => {
              const hasItem = configToEdit.some((item) => item.id === itemId);
              if (!hasItem) {
                // Insert missing item as hidden before Settings
                configToEdit.splice(settingsIndex, 0, {
                  id: itemId,
                  visible: false,
                  order: settingsIndex,
                });
              }
            });

            // Handle Nutrition based on allowedNutrition
            if (coach.allowedNutrition) {
              // If allowed, ensure it's in the config
              const hasNutrition = configToEdit.some(
                (item) => item.id === "CoachNutrition"
              );
              if (!hasNutrition) {
                // Insert Nutrition before Settings (which is always last)
                const currentSettingsIndex = configToEdit.findIndex(
                  (item) => item.id === "CoachSettings"
                );
                configToEdit.splice(currentSettingsIndex, 0, {
                  id: "CoachNutrition",
                  visible: false,
                  order: currentSettingsIndex,
                });
              }
            } else {
              // If not allowed, remove it from the config
              configToEdit = configToEdit.filter(
                (item) => item.id !== "CoachNutrition"
              );
            }

            // Update order properties
            configToEdit.forEach((item, idx) => {
              item.order = idx;
            });

            setTempSidebarConfig(configToEdit);
          }}
          onMouseEnter={() => !isMobile && setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          style={{
            opacity: isAppPartsHovered || isMobile ? 1 : 0,
            transition: "opacity 0.2s",
            marginTop: 8,
            paddingHorizontal: 16,
          }}
        >
          <Text
            style={{
              fontSize: 10,
              fontWeight: "400",
              color: isHovered
                ? "rgba(255, 255, 255, 0.6)"
                : "rgba(255, 255, 255, 0.4)",
            }}
          >
            {t("configureSidebar-string")}
          </Text>
        </TouchableOpacity>
      </View>
    );
  };

  const SidebarConfigItem = ({ item, index }) => {
    const [isHovered, setIsHovered] = useState(false);
    const canBeHidden = ![
      "CoachLibrary",
      "CoachDashboard",
      "CoachSettings",
    ].includes(item.id); // Library, Dashboard, and Settings are mandatory
    const canBeMoved = !["CoachDashboard", "CoachSettings"].includes(item.id); // Dashboard and Settings cannot be moved
    const ref = useRef(null);

    const getSectionName = (id) => {
      switch (id) {
        case "CoachDashboard":
          return "home-string";
        case "CoachLibrary":
          return "library-string";
        case "CoachBilling":
          return "billing-string";
        case "CoachCompetitions":
          return "competitions-string";
        case "CoachApplications":
          return "applications-string";
        case "CoachNutrition":
          return "nutrition-string";
        case "CoachSettings":
          return "settings-string";
        default:
          return "";
      }
    };

    const moveItem = (dragIndex, dropIndex) => {
      const newConfig = [...tempSidebarConfig];
      const draggedItem = newConfig[dragIndex];

      // Prevent moving Dashboard (index 0) or Settings (last index)
      if (dragIndex === 0 || dragIndex === newConfig.length - 1) {
        return;
      }

      // Prevent dropping at Dashboard or Settings positions
      if (dropIndex === 0 || dropIndex === newConfig.length - 1) {
        return;
      }

      newConfig.splice(dragIndex, 1);
      newConfig.splice(dropIndex, 0, draggedItem);

      // Update order property
      newConfig.forEach((item, idx) => {
        item.order = idx;
      });

      setTempSidebarConfig(newConfig);
    };

    const [{ isOver }, drop] = useDrop({
      accept: "SIDEBAR_ITEM",
      drop: (draggedItem, monitor) => {
        if (draggedItem.index !== index) {
          moveItem(draggedItem.index, index);
        }
      },
      collect: (monitor) => ({
        isOver: monitor.isOver(),
      }),
    });

    const [{ isDragging }, drag, dragPreview] = useDrag({
      type: "SIDEBAR_ITEM",
      item: {
        type: "SIDEBAR_ITEM",
        id: item.id,
        index,
      },
      canDrag: canBeMoved,
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    });

    const toggleVisibility = () => {
      if (!canBeHidden) return;
      const newConfig = [...tempSidebarConfig];
      newConfig[index].visible = !newConfig[index].visible;
      setTempSidebarConfig(newConfig);
    };

    // Combine drag and drop refs only for movable items
    if (canBeMoved) {
      drag(drop(ref));
    }

    return (
      <View
        ref={ref}
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          paddingVertical: 4,
          paddingHorizontal: 8,
          marginHorizontal: 0,
          marginVertical: 1,
          borderRadius: 12,
          backgroundColor:
            isOver && canBeMoved
              ? "rgba(255, 255, 255, 0.15)"
              : isHovered
              ? "rgba(255, 255, 255, 0.1)"
              : "transparent",
          opacity: isDragging ? 0 : 1,
          cursor: canBeMoved ? "move" : "default",
        }}
        onMouseEnter={() => !isMobile && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <View style={{ flexDirection: "row", alignItems: "center", flex: 1 }}>
          <MaterialIcons
            name="drag-indicator"
            size={18}
            color={canBeMoved ? Colors.grayColor : "transparent"}
            style={{ marginRight: 8 }}
          />

          <Text
            style={{
              color: item.visible ? Colors.whiteColor : Colors.grayColor,
              fontSize: 14,
              opacity: item.visible ? 1 : 0.5,
            }}
          >
            {t(getSectionName(item.id))}
          </Text>
        </View>

        <TouchableOpacity
          onPress={toggleVisibility}
          disabled={!canBeHidden}
          style={{ padding: 4 }}
        >
          <MaterialIcons
            name={item.visible ? "check-box" : "check-box-outline-blank"}
            size={20}
            color={canBeHidden ? Colors.whiteColor : Colors.grayColor}
            style={{ opacity: canBeHidden ? 1 : 0.5 }}
          />
        </TouchableOpacity>
      </View>
    );
  };

  const ConfigurationModal = () => {
    const [isSaveHovered, setIsSaveHovered] = useState(false);
    const [isCancelHovered, setIsCancelHovered] = useState(false);

    const handleSave = () => {
      // Filter out Dashboard and Settings - only save middle items
      const middleItemsToSave = tempSidebarConfig.filter(
        (item) => item.id !== "CoachDashboard" && item.id !== "CoachSettings"
      );

      // Save to coach.sidebarConfig
      coach.setProperty("sidebarConfig", [...middleItemsToSave]);
      // Persist to Firestore
      coach.uploadData({ sidebar_config: middleItemsToSave });
      setIsConfigMode(false);
      setIsAppPartsHovered(false);
    };

    const handleCancel = () => {
      // Revert to original full config (including Dashboard and Settings for display)
      const activeConfig = getActiveSidebarConfig();
      let fullConfig = activeConfig.map((item) => ({ ...item }));

      // Define the mandatory middle items that must always be present
      const mandatoryMiddleItems = [
        "CoachLibrary",
        "CoachBilling",
        "CoachCompetitions",
        "CoachApplications",
      ];

      // Find the Settings index (always last)
      const settingsIndex = fullConfig.findIndex(
        (item) => item.id === "CoachSettings"
      );

      // Check and add any missing mandatory items before Settings
      mandatoryMiddleItems.forEach((itemId) => {
        const hasItem = fullConfig.some((item) => item.id === itemId);
        if (!hasItem) {
          // Insert missing item as hidden before Settings
          fullConfig.splice(settingsIndex, 0, {
            id: itemId,
            visible: false,
            order: settingsIndex,
          });
        }
      });

      // Handle Nutrition based on allowedNutrition
      if (coach.allowedNutrition) {
        // If allowed, ensure it's in the config
        const hasNutrition = fullConfig.some(
          (item) => item.id === "CoachNutrition"
        );
        if (!hasNutrition) {
          // Insert Nutrition before Settings (which is always last)
          const currentSettingsIndex = fullConfig.findIndex(
            (item) => item.id === "CoachSettings"
          );
          fullConfig.splice(currentSettingsIndex, 0, {
            id: "CoachNutrition",
            visible: false,
            order: currentSettingsIndex,
          });
        }
      } else {
        // If not allowed, remove it from the config
        fullConfig = fullConfig.filter((item) => item.id !== "CoachNutrition");
      }

      // Update order properties
      fullConfig.forEach((item, idx) => {
        item.order = idx;
      });

      setTempSidebarConfig([...fullConfig]);
      setIsConfigMode(false);
      setIsAppPartsHovered(false);
    };

    return (
      <View
        style={{
          position: "absolute",
          top: 50,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "transparent",
          zIndex: 1000,
          flexDirection: "row",
          padding: 12,
          paddingTop: 24,
          backgroundColor: "#1E1E1E",
        }}
      >
        <View
          style={{
            flex: 1,
            width: "100%",
          }}
        >
          <View
            style={{
              marginBottom: 16,
            }}
          >
            <Text
              style={{
                fontSize: 14,
                fontWeight: "600",
                color: Colors.whiteColor,
                marginBottom: 6,
              }}
            >
              {t("customizeNavigation-string")}
            </Text>
            <Text
              style={{
                fontSize: 12,
                color: Colors.grayColor,
                lineHeight: 18,
              }}
            >
              {t("customizeNavigationDescription-string")}
            </Text>
          </View>

          <ScrollView
            style={{ flex: 1 }}
            contentContainerStyle={{
              flex: 1,
              justifyContent: "space-between",
            }}
            showsVerticalScrollIndicator={false}
          >
            <View style={{ width: "100%" }}>
              {tempSidebarConfig.map((item, index) => (
                <SidebarConfigItem key={item.id} item={item} index={index} />
              ))}
            </View>

            <View style={{ marginTop: 20, gap: 12 }}>
              <TouchableOpacity
                onPress={handleSave}
                onMouseEnter={() => !isMobile && setIsSaveHovered(true)}
                onMouseLeave={() => setIsSaveHovered(false)}
                style={{
                  padding: 10,
                  backgroundColor: Colors.bubbleColor,
                  borderRadius: 12,
                  alignItems: "center",
                  opacity: isSaveHovered ? 0.8 : 1,
                }}
              >
                <Text style={{ color: Colors.whiteColor, fontSize: 14 }}>
                  {t("save-string")}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={handleCancel}
                onMouseEnter={() => !isMobile && setIsCancelHovered(true)}
                onMouseLeave={() => setIsCancelHovered(false)}
                style={{
                  padding: 10,
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  borderRadius: 12,
                  alignItems: "center",
                  opacity: isCancelHovered ? 0.8 : 1,
                }}
              >
                <Text style={{ color: Colors.whiteColor, fontSize: 14 }}>
                  {t("cancel-string")}
                </Text>
              </TouchableOpacity>
            </View>
          </ScrollView>
        </View>
      </View>
    );
  };
  return (
    <View
      style={[
        styles.sideMenu,
        {
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: 220,
          height: "100%",
        },
      ]}
    >
      {isConfigMode && <ConfigurationModal />}

      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          marginTop: 24,
        }}
      >
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          <SvgImage source={WhitePng} width={160} style={{ marginLeft: 16 }} />
        </View>
      </View>

      <View style={{ width: "100%", flex: 1, marginTop: 2 }}>
        <View
          onMouseEnter={() => !isMobile && setIsAppPartsHovered(true)}
          onMouseLeave={() => !isMobile && setIsAppPartsHovered(false)}
        >
          <Observer>
            {() => {
              const activeConfig = getActiveSidebarConfig();
              return (
                <>
                  {/* <TeamCoachMenu /> */}

                  {activeConfig
                    .filter((item) => item.visible)
                    .filter(
                      (item) =>
                        item.id !== "CoachNutrition" || coach.allowedNutrition
                    )
                    .map((item) => (
                      <AppPartButton key={item.id} part={item.id} />
                    ))}

                  <ConfigureSidebarButton />
                </>
              );
            }}
          </Observer>
        </View>

        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            marginTop: 20,
            marginHorizontal: 12,
          }}
        >
          <Observer>
            {() => (
              <SearchBar
                text={coach.searchTermSideMenu}
                onChangeText={(text) => coach.setSearchTermSideMenu(text)}
                dark={true}
                placeholder={"searchClient-string"}
              />
            )}
          </Observer>
        </View>
        <View
          style={{
            marginTop: 10,
            width: "100%",
          }}
        >
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              borderRadius: 2,
              borderColor: "white",
              justifyContent: "space-between",
              paddingHorizontal: 12,
              marginBottom: 4,
            }}
          >
            <Text
              style={{
                color: Colors.grayColor,
                fontWeight: "400",
                fontSize: 12,
              }}
            >
              {t("athletes-string")}
            </Text>
            <View style={GlobalStyles.HStack}>
              <ResetClientsTextButton />
              <SortingMenu />
              <WeekDaysMenuSelector />
            </View>
          </View>
          <View
            style={{
              height: 0.5,
              backgroundColor: "rgba(255, 255, 255, 0.2)",
            }}
          />
        </View>
        <View style={{ flex: 1 }}>
          <Observer>
            {() => {
              // Check if there's a new version
              const currentVersion = getAppVersion();
              const lastSeenVersion = coach.lastSeenVersion || "0.0.0";
              const hasNewVersion =
                compareVersions(currentVersion, lastSeenVersion) > 0;

              // Memoize trial start timestamp
              const trialStartTimestamp = React.useMemo(() => {
                const startDate = coach?.trialPeriodStartDate;
                if (
                  !(startDate instanceof Date) ||
                  isNaN(startDate.getTime())
                ) {
                  return null;
                }
                return startDate.getTime();
              }, [coach?.trialPeriodStartDate?.getTime()]);

              const handleUpgradePress = React.useCallback(() => {
                coach.setProperty("sideMenuVisible", false);
                coach.setProperty("showingStripePayment", true);
              }, [coach]);

              return (
                <>
                  <ScrollView
                    style={{ flex: 1 }}
                    showsVerticalScrollIndicator={false}
                  >
                    {coach &&
                      coach
                        .getFilteredClientsSideMenu()
                        .map((client, index, array) => (
                          <View key={client.id}>
                            <ClientRow client={client} />
                            {index !== array.length - 1 && (
                              <View
                                style={{
                                  height: 1,
                                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                                  marginHorizontal: 12,
                                }}
                              />
                            )}
                          </View>
                        ))}
                  </ScrollView>

                  {coach.subscriptionStatus !== "active" ? (
                    <>
                      {coach.subscriptionStatus !== "past_due" && (
                        <>
                          <TutorialsButton />
                          <View
                            style={{
                              bottom: 0,
                              padding: 12,
                            }}
                          >
                            <FreeTrialCountdown
                              trialStartTimestamp={trialStartTimestamp}
                              onUpgradePress={handleUpgradePress}
                              t={t}
                              isMobile={isMobile}
                            />
                          </View>
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      {coach.clients.length === coach.maxClients &&
                        coach.showingUpgradePlan && (
                          <View
                            style={{
                              bottom: 0,
                              borderTopColor: "rgba(255, 255, 255, 0.2)",
                              borderTopWidth: 1,
                              backgroundColor: Colors.sideMenuColor,
                              padding: 12,
                            }}
                          >
                            <UpgradePlanDiscount />
                          </View>
                        )}
                    </>
                  )}

                  {/* What's New Button */}
                  {hasNewVersion && <WhatsNewButton />}
                </>
              );
            }}
          </Observer>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  sideMenu: {
    marginHorizontal: "auto",
    backgroundColor: "#1E1E1E",
  },
});

export default SideMenu;
